import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { LanguageAware } from '../language-aware';
import { ActivatedRoute, Router } from '@angular/router';
import { TZoneData } from 'src/app/models/zone-data';
import { TAreaData } from 'src/app/models/area-data';
import { EditSystemService } from 'src/app/services/edit-system.service';
import { ZoneService } from 'src/app/services/zone.service';
import { TEditableComponent } from 'src/app/models/editable-component';

@Component({
  selector: 'app-add-system-zone',
  templateUrl: './system-zone.component.html',
  styleUrls: ['./system-zone.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SystemZoneComponent extends LanguageAware implements OnInit {
  public workingWithActive = false;
  public showKeypad = false;
  private areaToUse: TAreaData = null;
  public maxZoneLen = 100;
  public visibleAreas: TAreaData[] = [];
  public canAssignZone = true;
  private readonly panicButtonsAreaStatus = 18;
  private ref: string | null = localStorage.getItem('ref');
  public readonly canEdit = this.systems.activeSystemHelper.can.edit.zones();

  public localZone: TZoneData = null;

  constructor(
    cdRef: ChangeDetectorRef,
    private es: EditSystemService,
    private router: Router,
    private zs: ZoneService,
    private r: ActivatedRoute,
  ) {
    super(cdRef);
    this.tag = 'Zone';
    this.workingWithActive = this.ref === null || this.ref !== 'new';
    const backUrl = this.workingWithActive ? '*' : '../add-system-zone-list';
    const isEditing = this.es.getId(TEditableComponent.Zone) !== -1;
    const headerText = isEditing ? this.trans('systems.titles.editZone') : this.trans('systems.buttons.addZone');
    this.headerBar.showHeader({
      headerText,
      backUrl,
      activatedRoute: this.r,
    });
    this.visibleAreas = this.getAreas();
    this.localZone = this.es.getEditableComponent(TEditableComponent.Zone);
    if (!isEditing) {
      let maxZone = 0;
      const zones = this.systems.activeSystem.zones;
      for (const iZone of zones) {
        if (iZone.queue_no > maxZone) {
          maxZone = iZone.queue_no;
        }
      }
      const localZoneName = this.systems.activeArea && this.systems.activeArea.status === this.panicButtonsAreaStatus
        ? this.trans('systems.titles.panicButton')
        : this.trans('systems.titles.zone');
      this.localZone = {
        id: 0,
        alarmed: false,
        areas: [],
        bypassed: false,
        enabled: true,
        failed: false,
        name: localZoneName.replace(':no', (maxZone + 1).toString()),
        queue_no: maxZone + 1,
        visible: true,
        native: false,
      };
      if ( this.visibleAreas.length > 0 ) {
        this.localZone.areas.push(this.visibleAreas[0].queue_no);
      }
    }
    if (this.systems.activeSystemHelper.can.edit.zones() || !this.workingWithActive) {
      this.footerBar.showFooter(this.trans('general.cancel'), this.trans(isEditing ? 'general.save' : 'general.add'), true, false);
      this.footerBar.onButton1Click = () => {
        if ( backUrl === '*' ) {
          history.back();
        } else {
          this.router.navigate([backUrl], { relativeTo: this.r, replaceUrl: true });
        }
      };
      this.footerBar.onButton2Click = () => {
        this.onNext();
      };
    }
    this.background.setGray();
    if (this.workingWithActive && this.systems.activeSystem.hwType === 'SP3') {
      this.maxZoneLen = 23;
    }
    this.canAssignZone = this.systems.activeSystem.hwType !== 'SP3' && !this.systems.activeSystem.directControl;
  }

  ngOnInit(): void {}

  public onNext() {
    if (this.localZone.queue_no < 1 || this.localZone.queue_no > 999) {
      this.toaster.postError(this.trans('systems.errors.badZoneNumber'));
      return;
    }
    if (this.localZone.areas.length === 0) {
      this.toaster.postError(this.trans('systems.errors.mustAssignZone'));
      return;
    }
    let zone: TZoneData = this.systems.activeSystem.zones.find((z) => z.queue_no === this.localZone.queue_no);
    const isNew = zone === null || zone === undefined;
    if (this.localZone.name === '') {
      this.toaster.postError(this.trans('validation.required').replace(':attribute', this.trans('systems.labels.zoneName')));
      return;
    }
    if (isNew) {
      zone = this.localZone;
    } else {
      zone.name = this.localZone.name;
      zone.queue_no = this.localZone.queue_no;
      zone.areas = this.localZone.areas;
      zone.visible = true;
    }

    if (isNew) {
      this.addZone(zone);
    } else {
      this.editZone(zone);
    }
    if ( this.workingWithActive ) {
      history.back();
    } else {
      this.router.navigate(['../add-system-zone-list'], { relativeTo: this.r, replaceUrl: true });
    }
  }

  private getAreas(): TAreaData[] {
    return this.systems.activeSystem.areas;
  }

  public zoneHasArea(value: number): boolean {
    return this.localZone.areas.filter((a) => a === value).length > 0;
  }

  public areaClick(value: number) {
    if ( !this.canEdit && this.workingWithActive ) { return; }
    if ( this.visibleAreas.length === 1 ) { return; }
    if (this.zoneHasArea(value)) {
      this.localZone.areas = this.localZone.areas.filter((a) => a !== value);
    } else {
      const temp = this.localZone.areas;
      temp.push(value);
      this.localZone.areas = temp;
    }
  }

  public getAssignedPgmName(forArea: TAreaData): string {
    const pgms = this.systems.activeSystem.pgms;
    for (const iPgm of pgms) {
      if (iPgm.control_area && iPgm.area_id === forArea.id) {
        return this.trans('systems.labels.controlledWith') + ': ' + iPgm.name;
      }
    }

    return '';
  }

  private addZone(zone: TZoneData) {
    if ( zone.areas.length === 0 && this.systems.activeSystem.areas.length > 0 ) {
      zone.areas.push(this.systems.activeSystem.areas[0].queue_no);
    }
    this.systems.activeSystem.zones.push(zone);
    this.systemZoneService.ingestZoneData(zone, this.systems.activeSystem.id);
    const activeSystemId = this.systems.activeSystem.id;
    this.api.post('/add/zone', { system_id: activeSystemId, zone }, true).subscribe(
      (result) => {
        if (result.success) {
          if ( result.id ) {
            zone.id = result.id;
          }
          if (this.systems.activeSystem.id === activeSystemId) {
            this.systems.setCurrentSystem(this.systems.activeSystem);
          }
        } else {
          this.revertAddition(activeSystemId, zone.queue_no);
          this.toaster.postError(result.error);
        }
      },
      (error) => {
        this.revertAddition(activeSystemId, zone.queue_no);
      }
    );
  }

  private revertAddition(activeSystemId: number, zoneNumber: number) {
    const system = this.systems.getSystem(activeSystemId);
    if (system === undefined) {
      return;
    }
    system.zones = system.zones.filter((z) => z.queue_no !== zoneNumber);
    if (this.systems.activeSystem.id === system.id) {
      this.systems.setCurrentSystem(system);
    }
  }

  private editZone(zone: TZoneData) {
    const activeSystemId = this.systems.activeSystem.id;
    const zoneBefore = this.es.getComponentBeforeModification(TEditableComponent.Zone);
    this.api.post('/edit/zone', { system_id: activeSystemId, zone }, true).subscribe(
      (result) => {
        if (result.success) {
          if (this.systems.activeSystem.id === activeSystemId) {
            this.systems.setCurrentSystem(this.systems.activeSystem);
          }
        } else {
          this.revertChanges(activeSystemId, zoneBefore);
          this.toaster.postError(result.error);
        }
      },
      (error) => {
        this.revertChanges(activeSystemId, zoneBefore);
        this.toaster.postError(this.trans('systems.errors.zoneSaveFailed'));
      }
    );
  }

  private revertChanges(systemId: number, zoneBefore: TZoneData) {
    const system = this.systems.getSystem(systemId);
    if (system === undefined) {
      return;
    }

    const zone = system.zones.find((z) => z.queue_no === zoneBefore.queue_no);
    if (zone === undefined) {
      return;
    } // Privalo egzistuot. O jeigu nėr, tai vadinasi buvo pašalintas kitos operacijos metu. Nepridedam.

    zone.name = zoneBefore.name;
    if (this.systems.activeSystem.id === systemId) {
      this.systems.setCurrentSystem(system);
    }
  }

  public queueNumberIsReadOnly(): boolean {
    return this.es.getComponentBeforeModification(TEditableComponent.Zone) !== null && this.workingWithActive;
  }

  public bypassFunctionAvailable(): boolean {
    if (!this.workingWithActive || this.systems.activeSystem === null || !this.systems.activeSystem.directControl) {
      return false;
    }
    let atLeastOneAreaHasBypass = false;
    for (const iZoneArea of this.localZone.areas) {
      const area = this.systems.activeSystem.areas.find((a) => a.queue_no === iZoneArea);
      if (area !== undefined && area.canBypass) {
        atLeastOneAreaHasBypass = true;
        break;
      }
    }

    return this.systems.activeSystemHelper.can.bypassZone() && atLeastOneAreaHasBypass;
  }

  public performBypass() {
    if (this.miniStatus.isVisible()) {
      this.miniStatus.flash();
      return;
    }
    if (this.localZone.bypassed && !this.systems.activeSystemHelper.can.unbypassZone()) {
      this.toaster.postError(this.trans('systems.errors.unbypassNotSupported'));
      return;
    }

    const area = this.systems.activeSystem.areas.find((a) => a.queue_no === this.localZone.areas[0]);
    if (area === undefined) {
      this.toaster.postError(this.trans('systems.errors.areaDoesNotExist'));
      return;
    }
    if (area.showKeypad) {
      this.areaToUse = area;
      this.showKeypad = true;
    } else {
      this.miniStatus.show(this.trans('general.pleaseWait'));
      const zoneBeingModified = this.localZone;
      this.zs.performBypass(this.localZone, undefined, undefined, area, (status: boolean) => {
        this.onBypassOk(zoneBeingModified, status);
      });
    }
  }

  public keypadCancelled() {
    this.showKeypad = false;
  }

  public pinConfirmed(pin?: string, remember?: boolean) {
    this.showKeypad = false;
    this.miniStatus.show(this.trans('general.pleaseWait'));
    const zoneBeingModified = this.localZone;
    this.zs.performBypass(this.localZone, pin, remember, this.areaToUse, (status: boolean) => {
      this.onBypassOk(zoneBeingModified, status);
    });
  }

  private onBypassOk(zone: TZoneData, newStatus: boolean) {
    if (!zone) {
      return;
    }
    if (this.systems.activeSystem.panel >= 0x10 && this.systems.activeSystem.panel < 0x20) {
      zone.bypassed = false;
      return;
    }
    zone.bypassed = newStatus;
    this.log('nauja zonos busena', zone);
  }

  public setZoneNumber(value: number) {
    if (isNaN(value)) {
      return;
    }
    if (typeof value === 'string') {
      if ((value as string) === '') {
        return;
      }
      value = parseInt(value, 10);
    }
    this.localZone.queue_no = value;
  }

  public zoneIsPanicButton(): boolean {
    const areas = this.getAreas();
    const PanicButtonAreas = areas.filter(area => area.status === this.panicButtonsAreaStatus).map(area => area.queue_no);
    const zoneHasPanicButtonArea = PanicButtonAreas.some(id => this.localZone.areas.includes(id));
    return zoneHasPanicButtonArea;
  }
}
